/**
 * Consent Manager Service
 *
 * Manages user cookie consent state with localStorage persistence.
 * Privacy-safe defaults: if anything goes wrong, treat as no consent.
 */

export interface ConsentState {
  marketing: boolean;
  updatedAt: number; // Unix timestamp in milliseconds
}

const STORAGE_KEY = 'cookie_consent_v1';
const EXPIRATION_DAYS = 365;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Read raw consent state from localStorage.
 * Returns null if missing, malformed, or localStorage is unavailable.
 */
function readConsentState(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof parsed.marketing !== 'boolean' ||
      typeof parsed.updatedAt !== 'number'
    ) {
      return null;
    }

    return parsed as ConsentState;
  } catch {
    return null;
  }
}

/**
 * Write consent state to localStorage.
 * Fails silently if localStorage is unavailable.
 */
function writeConsentState(state: ConsentState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable — fail silently (privacy-safe)
  }
}

/**
 * Check whether a consent timestamp is still within the 365-day window.
 */
function isTimestampValid(updatedAt: number): boolean {
  const now = Date.now();
  const age = now - updatedAt;
  return age < EXPIRATION_DAYS * MS_PER_DAY;
}

/**
 * Returns true if a valid (non-expired) consent decision exists.
 */
export function hasConsent(): boolean {
  const state = readConsentState();
  if (!state) return false;
  return isTimestampValid(state.updatedAt);
}

/**
 * Returns the current marketing consent value.
 * Returns false if no consent exists or consent has expired.
 */
export function getMarketingConsent(): boolean {
  const state = readConsentState();
  if (!state) return false;
  if (!isTimestampValid(state.updatedAt)) return false;
  return state.marketing;
}

/**
 * Store a marketing consent decision with the current timestamp.
 */
export function setMarketingConsent(granted: boolean): void {
  const state: ConsentState = {
    marketing: granted,
    updatedAt: Date.now(),
  };
  writeConsentState(state);
}

/**
 * Check if the stored consent is still valid (not expired).
 * Returns false if no consent exists.
 */
export function isConsentValid(): boolean {
  const state = readConsentState();
  if (!state) return false;
  return isTimestampValid(state.updatedAt);
}

/**
 * Clear all consent data from storage.
 */
export function clearConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — fail silently
  }
}
